import _ from "lodash";

// List of aggregate aux data identifiers that can be edited in the aggregate info section
export const aggregateAuxDataEditableList = ["rallyId", "EIMPPersonId"];

// Action constants specific to aggregate aux data
export const updateAggregateAuxData = "updateAggregateAuxData";
export const deleteAggregateAuxData = "deleteAggregateAuxData";
export const newAggregateAuxData = "newAggregateAuxData";

export const aggregateAuxDataSystemOptions = aggregateIdentifiers => {
  const identifiers = new Set(aggregateIdentifiers.map(i => i.system));
  const systemOptions = ["rallyId"];
  if (!identifiers.has("EIMPPersonId")) {
    systemOptions.push("EIMPPersonId");
  }
  return systemOptions;
};

// Function to update the aux data dialog context based on the action
export const identifierUpdateDialogContext = (detailsMap, section) => {
  if (section === "title") {
    switch (detailsMap["Mark as"]) {
      case "Replace":
        return "Replace override";
      case updateAggregateAuxData:
        return "Update Auxiliary Identifier";
      case deleteAggregateAuxData:
        return "Delete Auxiliary Identifier";
      case newAggregateAuxData:
        return "Add Auxiliary Identifier";
      default:
        return "";
    }
  }
  if (section === "description") {
    switch (detailsMap["Mark as"]) {
      case "Replace":
        return "Update the values below. Confirm to save changes.";
      case updateAggregateAuxData:
        return "Update the values below. Confirm to save changes.";
      case deleteAggregateAuxData:
        return "Are you sure you want to delete? Confirm below.";
      case newAggregateAuxData:
        return "Please verify the aggregate contains at least (1) eligible source before adding. Confirm to save changes.";
      default:
        return "";
    }
  }
};

// Request structure for updating the aggregate aux data. This is in the form of demographics and only includes all identifiers
// matching the target system. The request will contain all identifiers matching the target system, including the updated one.
//
// For example, if an aggregate has 3 RallyIds, and the user wants to update one of them,
// the request will contain all 3 RallyIds including the updated one. If the user wants to delete one of them, the request will
// contain the 2 remaining RallyIds. If the user wants to add a new RallyId, the request will contain the 3 existing RallyIds
// and the new RallyId. Identifiers matching the target system will be removed from the aggregate aux data and replaced with
// the new identifiers from the request. Should there be a delete request on a system matching only one identifier, the identifier
// will be removed and the aggregate aux data record may be deleted.
// This is communicated by including the system, but leaving the value empty: { system: "rallyId" }.
//
// EIMPPersonId will be handled similarly but with only one value as there should be one personId
// per aggregate. A delete of a EIMPPersonId may result in an automatic generation of a new EIMPPersonId. If a EIMPPersonId
// exists, a new one cannot be added.
export const prepareAggregateAuxDataRequest = (
  detailsMap,
  aggregateEimpLastUpdated,
  goldenId,
  identity
) => {
  const action = detailsMap["Mark as"];
  const deepCopyIdentity = { ...identity };
  const updatedIdentifier = { ...detailsMap.originalFieldData };
  const originalIdentifier = { ...detailsMap.originalFieldDataCopy };
  const system =
    action === newAggregateAuxData
      ? updatedIdentifier.system
      : originalIdentifier.system;

  // Starting template for every aggregateAuxIdentifierRequest. Includes the aggregateEimpLastUpdated timestamp
  // so it can be compared with the aggregate lastUpdated timestamp at the time of processing to determine if
  // the user is looking at an out of date version of the aggregate in the UI.
  const requestPayload = {
    resourceType: "Patient",
    id: goldenId,
    meta: {
      lastUpdated: aggregateEimpLastUpdated
    }
  };

  const currentAggregateIdentifiers = deepCopyIdentity.identifier;
  const identifiersUpdated = currentAggregateIdentifiers.filter(
    v => v.system === system
  );

  // If new aggregate aux identifier, add to the list of identifiers.
  if (action === newAggregateAuxData) {
    identifiersUpdated.push(updatedIdentifier);
  }

  // If delete aggregate aux identifier, find the target identifier and remove it from the list of identifiers.
  if (action === deleteAggregateAuxData) {
    const targetIndex = identifiersUpdated.findIndex(obj =>
      _.isEqual(obj, originalIdentifier)
    );
    identifiersUpdated.splice(targetIndex, 1);
  }

  // If update aggregate aux identifier, find the target identifier and replace it with the updated identifier.
  if (action === updateAggregateAuxData) {
    const targetIndex = identifiersUpdated.findIndex(obj =>
      _.isEqual(obj, originalIdentifier)
    );
    identifiersUpdated.splice(targetIndex, 1, updatedIdentifier);
  }

  // If after delete there are no identifiers, add the system back to the list so request processors knows to remove
  // all identifiers matching this system.
  if (_.isEmpty(identifiersUpdated)) {
    identifiersUpdated.identifier = { system: system };
  }

  requestPayload.identifier = Object.values(identifiersUpdated);

  return { ...requestPayload };
};
